import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { useJsApiLoader } from "@react-google-maps/api";

import "./App.css";
import "./AppBusiness.css";
import "./responsive.css";
import "react-toastify/dist/ReactToastify.css";

import UserDataLoader from "./components/UserDataLoader";
import BusinessRoutes from "./components/routerUtils/BusinessRoutes";
import BackToTop from "./components/BackToTop";

const GOOGLE_MAPS_API_KEY = "AIzaSyB_0KrF5mBXLiu7UmpiFG5KEC2eJO41u-M";
const libraries = ["places"];

// Lazy-loaded Public Screens
const Login = lazy(() => import("./screens/auth/Login"));
const ForgotPassword = lazy(() => import("./screens/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./screens/auth/ResetPassword"));
const RegisterClient = lazy(() => import("./screens/auth/RegisterClient"));
const RegisterBusiness = lazy(() => import("./screens/auth/RegisterBusiness"));
const ConfirmUser = lazy(() => import("./screens/auth/UserConfirmation"));

const HomeScreen = lazy(() => import("./screens/HomeScreen"));
const ViewOffer = lazy(() => import("./screens/ViewOffer"));
const FAQ = lazy(() => import("./screens/FAQ"));
const Contact = lazy(() => import("./screens/Contact"));
const PrivacyPolicy = lazy(() => import("./screens/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./screens/TermsOfService"));
const BrowseOffers = lazy(() => import("./screens/BrowseOffers"));
const BusinessesDirectory = lazy(() => import("./screens/BusinessesDirectory"));
const AboutUs = lazy(() => import("./screens/AboutUs"));

// Lazy-loaded Business Screens
const AdminDashboard = lazy(() => import("./screens/business/AdminDashboard"));
const CreateOffer = lazy(() => import("./screens/business/CreateOffer"));
const EditOffer = lazy(() => import("./screens/business/EditOffer"));
const ManageOffers = lazy(() => import("./screens/business/ManageOffers"));

const BusinessProfileScreen = lazy(
  () => import("./screens/business/BusinessProfile"),
);
const SocialMediaIntegration = lazy(
  () => import("./screens/business/SocialMediaIntegration"),
);
const ManageSubscription = lazy(
  () => import("./screens/business/ManageSubscription"),
);
const PostAnalytics = lazy(() => import("./screens/business/PostAnalytics"));
const CategoriesScreen = lazy(
  () => import("./screens/business/CategoriesScreen"),
);
const MallsScreen = lazy(() => import("./screens/business/MallsScreen"));

const NotFound = lazy(() => import("./screens/NotFound"));

const App = () => {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  if (!isLoaded) {
    return <div className="loading">Loading maps…</div>;
  }

  return (
    <>
      <Router>
        <UserDataLoader userInfo={userInfo}>
          <Suspense fallback={<div className="loading">Loading...</div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomeScreen />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register-client" element={<RegisterClient />} />
              <Route path="/register-business" element={<RegisterBusiness />} />
              <Route path="/confirm-user" element={<ConfirmUser />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/aboutus" element={<AboutUs />} />

              <Route
                path="/business-directory"
                element={<BusinessesDirectory />}
              />
              <Route path="/browse-offers" element={<BrowseOffers />} />
              <Route path="/offer/:hashKey/:postId" element={<ViewOffer />} />

              {/* Business Protected Routes */}
              <Route
                path="/business/*"
                element={
                  <BusinessRoutes>
                    <Routes>
                      <Route path="" element={<AdminDashboard />} />
                      <Route path="offers" element={<ManageOffers />} />
                      <Route path="offers/new" element={<CreateOffer />} />
                      <Route
                        path="offers/:hashKey/:postId/edit"
                        element={<EditOffer />}
                      />
                      <Route
                        path="business-profile"
                        element={<BusinessProfileScreen />}
                      />
                      <Route
                        path="social-media-settings"
                        element={<SocialMediaIntegration />}
                      />
                      <Route
                        path="manage-subscription"
                        element={<ManageSubscription />}
                      />
                      <Route path="analytics" element={<PostAnalytics />} />
                      <Route
                        path="analytics/:postId"
                        element={<PostAnalytics />}
                      />
                      <Route path="category" element={<CategoriesScreen />} />
                      <Route path="malls" element={<MallsScreen />} />
                    </Routes>
                  </BusinessRoutes>
                }
              />
              {/* 404 Not Found */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ToastContainer position="top-right" />
        </UserDataLoader>
      </Router>
      <BackToTop />
    </>
  );
};

export default App;
